import { useEffect, useState } from 'react'
import { KeyRound, Plus, Trash2, Terminal } from 'lucide-react'
import { PanelShell } from '../components/Layout'
import { Card, Button, Modal, Field, Empty, Rows, CopyButton, Badge } from '../components/UI'
import { get, post, del } from '../lib/api'
import { toast } from '../store'
import { clsx, faDate, shortAddr, asArray} from '../lib/format'

/**
 * نشانی پایه‌ی API از همان دامنه‌ای که کاربر الان روی آن است خوانده می‌شود.
 *
 * پیش‌تر اینجا زیردامنه‌ی «api» ثابت نوشته شده بود — و چنین
 * زیردامنه‌ای اصلاً وجود ندارد. API روی همان دامنه‌ی اصلی و زیر مسیر `/api`
 * سرو می‌شود. نمونه‌ی نادرست باعث می‌شد کاربر (و خودِ ما) دنبال دامنه‌ای
 * بگردد که هیچ‌وقت گواهی SSL نداشته.
 */
const apiBase = () =>
  (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ||
  (typeof window !== 'undefined' ? window.location.origin : 'https://arzinoo.com')

const sampleFor = (base: string) => `curl -X GET "${base}/api/market/tickers?quote=IRT" \\
  -H "X-API-KEY: az_xxxxxxxxxxxxxxxx"

# ثبت سفارش خرید
curl -X POST "${base}/api/orders" \\
  -H "X-API-KEY: az_xxxxxxxxxxxxxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{"symbol":"BTCIRT","side":"buy","type":"limit","price":5800000000,"amount":0.001}'`

const SAMPLE = sampleFor(apiBase())

export default function ApiKeys() {
  const [rows, setRows] = useState<any[] | null>(null)
  const [open, setOpen] = useState(false)
  const [created, setCreated] = useState<any>(null)
  const [form, setForm] = useState({ label: '', permissions: 'read', ip_whitelist: '' })
  const [loading, setLoading] = useState(false)

  const load = () => get('/user/api-keys').then((r: any) => setRows(asArray(r))).catch(() => setRows([]))
  useEffect(() => { load() }, [])  // نه useEffect(load) — load یک Promise برمی‌گرداند

  const create = async () => {
    setLoading(true)
    try {
      const r = await post('/user/api-keys', form)
      setCreated(r); setOpen(false); setForm({ label: '', permissions: 'read', ip_whitelist: '' }); load()
    } catch (e: any) { toast.err(e.message) } finally { setLoading(false) }
  }

  return (
    <PanelShell title="کلید API" subtitle="برای معامله برنامه‌نویسی‌شده و اتصال ربات‌های معاملاتی">
      <div className="mb-5 flex justify-end">
        <Button onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> ساخت کلید جدید</Button>
      </div>

      <Card className="!p-0">
        <div className="p-2">
          {rows === null ? <div className="p-3"><Rows n={3} /></div>
            : rows.length === 0 ? <Empty text="هنوز کلیدی نساخته‌اید" icon={<KeyRound className="h-6 w-6" />} />
            : rows.map((k) => (
              <div key={k.id} className="flex flex-wrap items-center gap-3 border-b border-white/[.05] p-4 last:border-0">
                <span className="grid h-10 w-10 place-items-center rounded-xl border border-brand-500/25 bg-brand-500/10 text-brand-300">
                  <KeyRound className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white">{k.label}</div>
                  <div className="num text-[11px] text-slate-500" dir="ltr">{shortAddr(k.key, 14)}</div>
                </div>
                <Badge kind={k.permissions === 'read' ? 'muted' : 'gold'}>
                  {k.permissions === 'read' ? 'فقط خواندن' : 'خواندن و معامله'}
                </Badge>
                <span className="text-[11px] text-slate-500">{faDate(k.createdAt, false)}</span>
                <div className="mr-auto flex items-center gap-2">
                  <CopyButton text={k.key} label="کپی کلید" />
                  <button onClick={async () => { await del(`/user/api-keys/${k.id}`); toast.ok('کلید حذف شد'); load() }}
                    className="rounded-lg p-2 text-slate-500 transition hover:bg-down/10 hover:text-down">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
        </div>
      </Card>

      <Card className="mt-5">
        <h3 className="mb-4 flex items-center gap-2 font-bold text-white"><Terminal className="h-4 w-4 text-brand-300" /> نمونه استفاده</h3>
        <pre className="num overflow-x-auto rounded-xl border border-white/[.06] bg-ink-950/80 p-4 text-[11px] leading-6 text-slate-300" dir="ltr">
{SAMPLE}
        </pre>
        <p className="mt-3 text-xs leading-6 text-slate-500">
          مستندات کامل و تعاملی API در مسیر <span className="num text-brand-300" dir="ltr">/api/docs</span> در دسترس است.
        </p>
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title="ساخت کلید API">
        <Field label="عنوان کلید">
          <input className="input" placeholder="ربات معامله‌گر" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
        </Field>
        <Field label="سطح دسترسی">
          <div className="flex gap-2">
            {[['read', 'فقط خواندن'], ['trade', 'خواندن و معامله']].map(([v, l]) => (
              <button key={v} onClick={() => setForm({ ...form, permissions: v })}
                className={clsx('chip', form.permissions === v && 'border-brand-500/50 bg-brand-500/15 text-brand-200')}>{l}</button>
            ))}
          </div>
        </Field>
        <Field label="محدودیت IP (اختیاری)" hint="چند IP را با کاما جدا کنید">
          <input className="input num" dir="ltr" placeholder="1.2.3.4, 5.6.7.8" value={form.ip_whitelist}
            onChange={(e) => setForm({ ...form, ip_whitelist: e.target.value })} />
        </Field>
        <Button loading={loading} onClick={create} className="w-full !py-3">ایجاد کلید</Button>
      </Modal>

      <Modal open={!!created} onClose={() => setCreated(null)} title="کلید شما ساخته شد">
        <div className="mb-4 rounded-xl border border-gold/20 bg-gold/[.06] p-3 text-[11px] leading-6 text-gold/90">
          مقدار Secret تنها همین یک‌بار نمایش داده می‌شود. آن را در جای امنی ذخیره کنید.
        </div>
        {[['API Key', created?.key], ['Secret', created?.secret]].map(([label, val]) => (
          <div key={label as string} className="mb-3">
            <div className="label">{label}</div>
            <div className="flex items-center gap-2">
              <div className="num flex-1 overflow-x-auto rounded-xl border border-white/[.07] bg-ink-950/70 px-3 py-2.5 text-xs text-slate-200" dir="ltr">
                {val}
              </div>
              <CopyButton text={String(val ?? '')} label="" />
            </div>
          </div>
        ))}
        <Button className="mt-3 w-full" onClick={() => setCreated(null)}>ذخیره کردم</Button>
      </Modal>
    </PanelShell>
  )
}
